import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshBilling = useCallback(async () => {
        try {
            const b = await api.billingMe();
            setBilling(b);
            return b;
        } catch (err) {
            // /billing/me is optional side info — never block auth on this
            console.warn("billing/me fetch failed:", err?.message || err);
            setBilling(null);
            return null;
        }
    }, []);

    const refresh = useCallback(async () => {
        try {
            const me = await api.me();
            setUser(me);
            await refreshBilling();
        } catch (err) {
            // /me returns 401 when unauthenticated — that's the "not logged in" path
            if (err?.message && !/401|Invalid session|Not authenticated|Session expired/i.test(err.message)) {
                console.warn("auth/me failed:", err.message);
            }
            setUser(null);
            setBilling(null);
        } finally {
            setLoading(false);
        }
    }, [refreshBilling]);

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } catch (err) {
            // Best-effort — clear local state even if the server call fails
            console.warn("logout call failed:", err?.message || err);
        }
        setUser(null);
        setBilling(null);
    }, []);

    const value = useMemo(
        () => ({ user, billing, loading, setUser, refresh, refreshBilling, logout }),
        [user, billing, loading, refresh, refreshBilling, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
